import { useEffect, useState } from "react";
import { getPortfolioHealth } from "./StockTradeService";
import { getWatchlist } from "./StockWatchService";

const fmt = (num) => {
  if (num == null || isNaN(num)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(num);
};

const pct = (num) => {
  if (num == null || isNaN(num)) return "—";
  return (num >= 0 ? "+" : "") + Number(num).toFixed(2) + "%";
};

const alertStatus = (w) => {
  const cur = Number(w.currentBuyingPrice || 0);
  const buy = w.targetBuyPrice != null ? Number(w.targetBuyPrice) : null;
  const sell = w.targetSellPrice != null ? Number(w.targetSellPrice) : null;
  if (sell != null && cur >= sell) return "sell";
  if (buy != null && cur <= buy) return "buy";
  return "ok";
};

const CONCEPTS = [
  {
    title: "What is a Stock?",
    tag: "Basics",
    body: "A stock (share) is a small ownership piece of a company. When you buy a stock, you become a part-owner. If the company grows in value, your stock is worth more. In India, companies list on NSE (National Stock Exchange) or BSE (Bombay Stock Exchange).",
  },
  {
    title: "When to Buy",
    tag: "Decisions",
    body: "Buy when you believe the company's future value is higher than its current price. Good times to buy: when the stock is near your target buy price, when the sector is temporarily down but the company is fundamentally strong, or when you're starting a long-term investment. Never buy in panic or FOMO.",
  },
  {
    title: "When to Sell",
    tag: "Decisions",
    body: "Sell when your profit target (target sell price) is reached. Also consider selling if the reason you bought the stock no longer holds — e.g., the company's business outlook has changed, or the sector is in long-term decline. Holding a loss indefinitely hoping for recovery is a common trap.",
  },
  {
    title: "What is P&L (Profit & Loss)?",
    tag: "Basics",
    body: "Realized P&L = profit or loss from trades you've already closed. Unrealized P&L = paper profit/loss on holdings you still own. Unrealized only becomes real when you sell. Total P&L = Realized + Unrealized.",
  },
  {
    title: "Large Cap, Mid Cap, Small Cap",
    tag: "Portfolio",
    body: "Large-cap stocks (NIFTY 50 companies like Reliance, TCS, HDFC) are big, stable, lower-risk. Mid-cap are growing companies with moderate risk. Small-cap are smaller companies — higher potential returns but more volatile. A balanced portfolio holds a mix — typically 60% large, 30% mid, 10% small.",
  },
  {
    title: "Sector Diversification",
    tag: "Portfolio",
    body: "Diversification = spread your money across different sectors (IT, Finance, Energy, FMCG, Pharma, etc.) so a crash in one sector doesn't hurt your whole portfolio. Rule of thumb: no single sector should be more than 30–35% of your portfolio.",
  },
  {
    title: "What is a Watchlist?",
    tag: "Tools",
    body: "Your watchlist is stocks you're interested in but haven't bought yet. Set a Target Buy Price (what you're willing to pay) and Target Sell Price (your profit goal). When current price hits your buy target → the system shows a BUY ALERT. When it hits your sell target → SELL ALERT.",
  },
  {
    title: "Understanding Price Changes",
    tag: "Tools",
    body: "In the Performance/Alerts tab, you see price changes over 15 days, 1 month, 3 months (Q), 6 months, 1 year. A stock consistently falling across all timeframes needs review. A stock up in short-term but flat/down over 1 year may be a temporary bounce, not a trend.",
  },
  {
    title: "Stop Loss — Protect Yourself",
    tag: "Risk",
    body: "A stop loss is a price below which you decide to sell to cap your loss. Example: Buy at ₹100, set stop loss at ₹90. If it falls to ₹90, sell — limiting loss to 10%. This protects against a stock falling much further. Many traders use 8–10% as a stop loss level.",
  },
  {
    title: "NSE & BSE — What Are They?",
    tag: "India",
    body: "NSE (National Stock Exchange) and BSE (Bombay Stock Exchange) are India's two main stock exchanges. NIFTY 50 = top 50 companies on NSE by market cap. SENSEX = top 30 on BSE. When NIFTY is up, it means the market is broadly going up. When NIFTY is down, most stocks typically fall too.",
  },
  {
    title: "Long-term vs Short-term",
    tag: "Strategy",
    body: "Long-term (1+ years): Ride out fluctuations, benefit from compounding, pay lower LTCG tax (10% above ₹1L gain). Short-term (under 1 year): Higher STCG tax (15%), needs more monitoring. For beginners, starting with long-term large-cap investing is generally safer.",
  },
  {
    title: "Portfolio Concentration Risk",
    tag: "Risk",
    body: "If one stock makes up 30%+ of your portfolio, a 30% fall in that stock drops your whole portfolio by 9%. Monitor top-1 and top-3 concentration — if the top 3 stocks exceed 50% of your portfolio, consider diversifying. The Risk Signals section in your Summary tab will flag this.",
  },
];

const TAG_COLOR = {
  Basics: "blue", Decisions: "green", Portfolio: "indigo",
  Tools: "purple", Risk: "red", India: "orange", Strategy: "teal",
};

export default function TradeGuide() {
  const [health, setHealth] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [activeTag, setActiveTag] = useState("ALL");

  useEffect(() => {
    Promise.all([
      getPortfolioHealth().catch(() => null),
      getWatchlist().catch(() => []),
    ]).then(([h, w]) => {
      setHealth(h);
      setWatchlist(Array.isArray(w) ? w : []);
    });
  }, []);

  const buyAlerts = watchlist.filter(w => alertStatus(w) === "buy");
  const sellAlerts = watchlist.filter(w => alertStatus(w) === "sell");
  const riskSignals = health?.riskSignals || [];

  const tags = ["ALL", ...new Set(CONCEPTS.map(c => c.tag))];
  const visibleConcepts = activeTag === "ALL" ? CONCEPTS : CONCEPTS.filter(c => c.tag === activeTag);

  const totalPnlPct = health && health.totalInvested > 0
    ? ((health.totalPnl / health.totalInvested) * 100)
    : null;

  return (
    <div className="stocks-section">

      {/* PORTFOLIO SNAPSHOT */}
      {health && (
        <div className="guide-snapshot">
          <div className="guide-snap-item">
            <div className="guide-snap-label">Invested</div>
            <div className="guide-snap-value">{fmt(health.totalInvested)}</div>
          </div>
          <div className="guide-snap-item">
            <div className="guide-snap-label">Current value</div>
            <div className="guide-snap-value">{fmt(health.currentValue)}</div>
          </div>
          <div className={`guide-snap-item${health.totalPnl >= 0 ? " guide-snap-green" : " guide-snap-red"}`}>
            <div className="guide-snap-label">Total P&L</div>
            <div className="guide-snap-value">
              {fmt(health.totalPnl)}
              {totalPnlPct != null && <span className="guide-snap-pct"> ({pct(totalPnlPct)})</span>}
            </div>
          </div>
          <div className="guide-snap-item">
            <div className="guide-snap-label">Holdings</div>
            <div className="guide-snap-value">{(health.topPositions || []).length} stocks</div>
          </div>
        </div>
      )}

      {/* PERSONALIZED ALERTS */}
      {(buyAlerts.length > 0 || sellAlerts.length > 0 || riskSignals.length > 0) && (
        <div className="guide-advice">
          <div className="guide-advice-title">💡 Actions for You Right Now</div>

          {buyAlerts.map(w => {
            const gainPct = w.targetSellPrice && w.currentBuyingPrice
              ? (((w.targetSellPrice - w.currentBuyingPrice) / w.currentBuyingPrice) * 100).toFixed(1)
              : null;
            return (
              <div key={w.id} className="guide-alert guide-alert-buy">
                <div className="guide-alert-head">🟢 BUY SIGNAL — {w.symbol}</div>
                <div className="guide-alert-body">
                  Current price <strong>₹{w.currentBuyingPrice}</strong> has reached your target buy price of <strong>₹{w.targetBuyPrice}</strong>.
                  {gainPct && ` Your target sell is ₹${w.targetSellPrice} — potential gain of ${gainPct}%.`}
                  {w.remarks && ` Note: ${w.remarks}`}
                </div>
              </div>
            );
          })}

          {sellAlerts.map(w => (
            <div key={w.id} className="guide-alert guide-alert-sell">
              <div className="guide-alert-head">🔴 SELL SIGNAL — {w.symbol}</div>
              <div className="guide-alert-body">
                Current price <strong>₹{w.currentBuyingPrice}</strong> has reached your sell target of <strong>₹{w.targetSellPrice}</strong>.
                Consider taking profits now.
                {w.remarks && ` Note: ${w.remarks}`}
              </div>
            </div>
          ))}

          {riskSignals.map(s => (
            <div key={s.code} className={`guide-alert guide-alert-${(s.severity || "low").toLowerCase()}`}>
              <div className="guide-alert-head">⚠️ {s.title}</div>
              <div className="guide-alert-body">{s.message}</div>
            </div>
          ))}
        </div>
      )}

      {!health && !watchlist.length && (
        <div className="guide-onboard">
          <div className="guide-onboard-icon">🚀</div>
          <div className="guide-onboard-title">Start your trading journey</div>
          <div className="guide-onboard-sub">
            Add stocks to your portfolio via the Trades tab, then add stocks you're watching to the Watchlist.
            This page will then show you personalized buy/sell signals and risk alerts.
          </div>
        </div>
      )}

      {/* LEARNING CONCEPTS */}
      <div className="guide-learn-head">
        <span>📚 Trading Concepts</span>
        <div className="guide-tag-row">
          {tags.map(t => (
            <button
              key={t}
              className={`guide-tag-btn${activeTag === t ? " active" : ""}`}
              onClick={() => setActiveTag(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="guide-concepts">
        {visibleConcepts.map((c, idx) => {
          const globalIdx = CONCEPTS.indexOf(c);
          const isOpen = expanded === globalIdx;
          return (
            <div
              key={c.title}
              className={`guide-concept${isOpen ? " guide-concept-open" : ""}`}
              onClick={() => setExpanded(isOpen ? null : globalIdx)}
            >
              <div className="guide-concept-head">
                <span className="guide-concept-title">{c.title}</span>
                <div className="guide-concept-right">
                  <span className={`guide-concept-tag guide-ctag-${TAG_COLOR[c.tag] || "blue"}`}>{c.tag}</span>
                  <span className="guide-concept-toggle">{isOpen ? "−" : "+"}</span>
                </div>
              </div>
              {isOpen && <div className="guide-concept-body">{c.body}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
